import React from "react";
import { Field, FormSection } from "redux-form";
import { Collapse, Icon } from "antd";

import FixedDocumentProjectDetailsSection from "./FixedDocumentProjectDetailsSection.jsx";
import FixedDocumentAuthorizationSection from "./FixedDocumentAuthorizationSection.jsx";
import DefaultHighRiskActivitiesSection from "./DefaultHighRiskActivitiesSection.jsx";
import DefaultRiskMatrixSection from "./DefaultRiskMatrixSection.jsx";
import DefaultEmergencyContactsSection from "./DefaultEmergencyContactsSection.jsx";
import DynamicSwmsContentManagementSection from "./DynamicSwmsContentManagementSection.jsx";
import DynamicMethodItemTypeSection from "./DynamicMethodItemTypeSection.jsx";
import { CustomSwitch } from "../../../../../../common/customSwitch";

import styles from "../../../../JobStyle.module.css";

const { Panel } = Collapse;

const FIXED_SECTION_KEYS = {
  document_project_details: "Document and Project Details",
  document_authorization: "Document Authorization"
};

const DEFAULT_SECTION_KEYS = {
  high_risk_activities: "High Risk Activities",
  risk_matrix: "Risk Assessment Matrix",
  job_steps_hazards_consequences_controls: "Job Steps, Hazards, Consequences & Controls",
  emergency_contacts: "Emergency Contacts"
};

const formatSectionLabel = (sectionKey, sectionData) => {
  if (sectionData && sectionData.display_name) {
    return sectionData.display_name;
  }

  return sectionKey
    .split("_")
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(" ");
};

const resolveSectionConfig = (sectionKey, sectionData) => {
  const sectionType = sectionData && sectionData.type ? sectionData.type : "unknown";

  if (FIXED_SECTION_KEYS[sectionKey]) {
    return {
      bucket: "fixed",
      heading: FIXED_SECTION_KEYS[sectionKey],
      sectionType
    };
  }

  if (DEFAULT_SECTION_KEYS[sectionKey]) {
    return {
      bucket: "default",
      heading: DEFAULT_SECTION_KEYS[sectionKey],
      sectionType
    };
  }

  if (sectionType === "method_item_type" || sectionKey.startsWith("method_item_type_")) {
    return {
      bucket: "method_item_type",
      heading: formatSectionLabel(sectionKey, sectionData),
      sectionType: "method_item_type"
    };
  }

  if (sectionType === "swms_content_management" || sectionKey.startsWith("swms_content_management_")) {
    return {
      bucket: "swms_content_management",
      heading: formatSectionLabel(sectionKey, sectionData),
      sectionType: "swms_content_management"
    };
  }

  return {
    bucket: "unsupported",
    heading: formatSectionLabel(sectionKey, sectionData),
    sectionType
  };
};

const sectionBodyMessageByBucket = {
  fixed: "Fixed section placeholder.",
  default: "Default section placeholder.",
  method_item_type: "Dynamic Method Item Type placeholder. Dedicated component wiring is next.",
  swms_content_management: "Dynamic SWMS Content Management placeholder. Dedicated component wiring is next.",
  unsupported: "Unsupported section placeholder. Section key is preserved safely."
};

const renderSectionContent = (sectionKey, sectionData, swmsData, loading, change, config) => {
  if (config.bucket === "fixed") {
    if (sectionKey === "document_project_details") {
      return (
        <FormSection
          component={FixedDocumentProjectDetailsSection}
          name={`sections.${sectionKey}.data`}
          sectionKey={sectionKey}
          sectionData={sectionData}
          swmsData={swmsData}
          loading={loading}
        />
      );
    }

    if (sectionKey === "document_authorization") {
      return (
        <FixedDocumentAuthorizationSection
          sectionKey={sectionKey}
          sectionData={sectionData}
          loading={loading}
          change={change}
        />
      );
    }
  }

  if (config.bucket === "default") {
    if (sectionKey === "high_risk_activities") {
      return (
        <FormSection
          component={DefaultHighRiskActivitiesSection}
          name={`sections.${sectionKey}.data`}
          sectionKey={sectionKey}
          sectionData={sectionData}
          loading={loading}
        />
      );
    }

    if (sectionKey === "risk_matrix") {
      return (
        <FormSection
          component={DefaultRiskMatrixSection}
          name={`sections.${sectionKey}.data`}
          sectionKey={sectionKey}
          sectionData={sectionData}
          swmsData={swmsData}
          loading={loading}
        />
      );
    }

    if (sectionKey === "emergency_contacts") {
      return (
        <FormSection
          component={DefaultEmergencyContactsSection}
          name={`sections.${sectionKey}.data`}
          sectionKey={sectionKey}
          sectionData={sectionData}
          loading={loading}
          change={change}
        />
      );
    }
  }

  if (config.bucket === "method_item_type") {
    return (
      <FormSection
        component={DynamicMethodItemTypeSection}
        name={`sections.${sectionKey}.data`}
        sectionKey={sectionKey}
        sectionData={sectionData}
        loading={loading}
        change={change}
      />
    );
  }

  if (config.bucket === "swms_content_management") {
    return (
      <FormSection
        component={DynamicSwmsContentManagementSection}
        name={`sections.${sectionKey}.data`}
        sectionKey={sectionKey}
        sectionData={sectionData}
        loading={loading}
        change={change}
      />
    );
  }

  // Placeholder for unimplemented sections
  return (
    <div style={{ fontSize: "12px", color: "#475569", padding: "8px 0" }}>
      {sectionBodyMessageByBucket[config.bucket]}
    </div>
  );
};

export default function JobSwmsFormV3SectionRegistry({ swmsData, loading, change }) {
  const sectionEntries = swmsData && swmsData.sections && typeof swmsData.sections === "object"
    ? Object.entries(swmsData.sections)
    : [];

  if (sectionEntries.length === 0) {
    return <div className="text-muted">No sections found in draft data.</div>;
  }

  return (
    <Collapse
      accordion
      defaultActiveKey="document_project_details"
      bordered={false}
      expandIconPosition="right"
      style={{ marginTop: "10px" }}
      className="custom-section-v2-collapse custom-job-swms-collapse"
      expandIcon={({ isActive }) => (
        <Icon type="caret-right" rotate={isActive ? 90 : 0} style={{ color: "#2f1f41" }} />
      )}
    >
      {sectionEntries.map(([sectionKey, sectionData]) => {
        const config = resolveSectionConfig(sectionKey, sectionData);
        const content = renderSectionContent(sectionKey, sectionData, swmsData, loading, change, config);
        return (
          <Panel
            key={sectionKey}
            className="custom-job-swmsv3-collapse-panel"
            style={{ border: "0px !important" }}
            header={
              <div className={`${styles.BackgroundSectionHeading} custom-section-v2-panel-heading`} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {config.bucket === "fixed" ? (
                  <span aria-hidden="true" style={{ display: "inline-flex", color: "#362148" }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" width="14" height="14">
                      <path fill="currentColor" d="M256 160L256 224L384 224L384 160C384 124.7 355.3 96 320 96C284.7 96 256 124.7 256 160zM192 224L192 160C192 89.3 249.3 32 320 32C390.7 32 448 89.3 448 160L448 224C483.3 224 512 252.7 512 288L512 512C512 547.3 483.3 576 448 576L192 576C156.7 576 128 547.3 128 512L128 288C128 252.7 156.7 224 192 224z" />
                    </svg>
                  </span>
                ) : null}
                {(sectionKey === "high_risk_activities" || sectionKey === "risk_matrix" || sectionKey === "emergency_contacts" || config.bucket === "method_item_type" || config.bucket === "swms_content_management") ? (
                  <span aria-hidden="true" style={{ display: "inline-flex", color: "#362148", opacity: 0.8 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512" width="14" height="14">
                      <path fill="currentColor" d="M40 352l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zm192 0l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 320c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 192l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40zM40 160c-22.1 0-40-17.9-40-40L0 72C0 49.9 17.9 32 40 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0zM232 32l48 0c22.1 0 40 17.9 40 40l0 48c0 22.1-17.9 40-40 40l-48 0c-22.1 0-40-17.9-40-40l0-48c0-22.1 17.9-40 40-40z" />
                    </svg>
                  </span>
                ) : null}
                {config.heading}
                {(config.bucket === "swms_content_management" || config.bucket === "method_item_type") ? (
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className="float-right position-relative"
                    style={{ right: "30px", marginLeft: "auto" }}
                  >
                    <Field
                      name={`sections.${sectionKey}.data.toggle`}
                      size="small"
                      className="cs-switch-v3"
                      component={CustomSwitch}
                      disabled={loading}
                    />
                  </div>
                ) : null}
              </div>
            }
          >
            {content}
          </Panel>
        );
      })}
    </Collapse>
  );
}
